import useResizeObserver from "@react-hook/resize-observer";
import React, { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import { useImmerReducer } from "use-immer";

import { getContextsSafely } from "@examspark/api";
import { coloredTableStyles } from "@examspark/common-ui";
import {
  Canvas,
  QuestionContext,
  reducer,
  Action,
  QuestionState,
} from "@examspark/question-player";
import { ExamBlobs, ExamSessionMetadata } from "@examspark/types";

import { Highlighter } from "../../utils/highlighter";
import { useExistingHighlights } from "../../utils/useHighlighter";
import { LineReaderContext, useLineReader } from "../../utils/useLineReader";
import Button from "../common/Button";
import HighlighterToolbar from "../common/tools/Highlighter";

import ItemContextModal from "./ItemContextModal";
import ItemContextSidebar from "./ItemContextSidebar";

const StyledItem = styled.div`
  display: flex;
  height: 100%;
  min-height: 0;
  background-color: #fff;

  & * {
    box-sizing: border-box;
  }

  display: flex;
  background-color: #fff;
  height: 100%;
  flex: 1;
  /* font-size: 1.2em; */

  .choice-drag {
    width: 2.8rem;
    padding-left: 0.5rem;
  }
  .choice-drag > div {
    width: 2.1rem;
    cursor: ns-resize;
    /* background-image: url("/move-handle-v.png"); */
    background-size: 11px 24px;
    background-repeat: no-repeat;
    background-position: center 5px;
    opacity: 0.5;
    min-height: 34px;
  }
  .choice-drag > div:hover {
    background-color: #e7e7e7;
    opacity: 1;
  }

  .gap {
    background-color: #fff;
    /* padding: 0 0.5rem; */
    border: dashed 2px #ccc;
    min-height: 2rem;
    width: 5rem;
    transition: border-color 0.2s;
    display: inline-block;
    vertical-align: middle;
    position: relative;
    margin: 0 0.25rem;
  }
  .gap .remove {
    position: absolute;
    height: 1.7rem;
    width: 1.7rem;
    cursor: pointer;
  }
  .gap p {
    margin: 0;
  }
  .gap .remove:hover {
    background-color: #aaa;
  }
  .gap.is-drop-over {
    border: dashed 2px #006699;
  }
  .gap.is-filled {
    width: auto;
  }
  .gap .markup {
    position: absolute;
    right: -1rem;
    top: -1rem;
  }

  img {
    max-width: 100%;
  }

  ${coloredTableStyles}
`;

const ItemMain = styled.div`
  flex: 1;
  overflow: auto;
  height: 100%;
`;

const ItemPassageToolbar = styled.div`
  padding-bottom: 1rem;
  border-bottom: solid 1px #ddd;
`;

const SecondaryButton = styled(Button)`
  background-color: #e7e7e7;
  color: #333;
  cursor: pointer;
  padding: 0 1rem;
  /* border-radius: 100%; */
  /* box-shadow: 0.5rem 0.5rem 0.7rem rgba(0, 0, 0, 0.3); */

  &:hover {
    background-color: #e7e7e7;
  }
`;

const ItemNumber = styled.div`
  display: flex;
  font-size: 1.2rem;
  color: #666;

  & > span {
    border-bottom: solid 1px #ddd;
    padding: 0.5rem 0;
    /* border-radius: 0.5rem; */

    span {
      margin-left: 0.5rem;
    }
  }
`;

interface Props {
  data: any;
  sessionData?: ExamSessionMetadata;

  response?: any;
  onChange?: (rspData: any) => void;

  blobs?: ExamBlobs[string];
  onBlobChange?: (blobData: ExamBlobs[string]) => void;
}

const Item: React.FC<Props> = (props) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<any>();
  const [isContextModalOpen, setIsContextModalOpen] = useState(false);
  const [state, dispatch] = useImmerReducer<QuestionState, Action>(reducer, {
    response: {},
  });

  // Where the magic happens
  useResizeObserver(ref, (entry) => {
    setSize(entry.contentRect);
  });

  const { handleClick, containerRef } = useLineReader();
  const { activeLineReader } = useContext(LineReaderContext) ?? {};
  const highlighterRef = React.useRef<Highlighter | null>(null);
  const { onHighlighterInit } = useExistingHighlights();

  const contexts = getContextsSafely(state.question?.contexts);

  useEffect(() => {
    if (!props.sessionData?.shuffleSeed) return;

    // shuffle
    if (props.sessionData.shuffleOptions && !state.shuffleSeed) {
      dispatch({
        type: "SHUFFLE",
        seed: props.sessionData.shuffleSeed,
      });
    }
    // unshuffle
    else if (!props.sessionData.shuffleOptions && state.shuffleSeed) {
      dispatch({
        type: "SHUFFLE",
        seed: undefined,
      });
    }
  }, [dispatch, state, props.sessionData]);

  useEffect(() => {
    if (!props.data?.id) return;

    dispatch({
      type: "INIT",
      data: {
        question: props.data,
        response: props.response,
        blobs: props.blobs,
      },
    });
  }, [dispatch, props.data?.id, props.data?.updatedAt]);

  // set responses on the ExamState
  useEffect(() => {
    // if (state.response && !Object.keys(state.response).length) return;
    props.onChange?.(state.response);
  }, [state.response]);

  // set Blob data on the ExamState
  useEffect(() => {
    props.onBlobChange?.(state.blobs || {});
  }, [state.blobs]);

  return (
    <QuestionContext.Provider value={[state, dispatch]}>
      <ItemContextModal
        isOpen={isContextModalOpen}
        onRequestClose={() => setIsContextModalOpen(false)}
        contexts={contexts}
      />
      <StyledItem ref={ref}>
        {size?.width >= 800 && !!contexts.length && (
          <ItemContextSidebar contexts={contexts} />
        )}
        <ItemMain>
          {size?.width < 800 && !!contexts.length && (
            <ItemPassageToolbar>
              <SecondaryButton
                onClick={() => {
                  // unmount the line reader if it's active
                  // before opening the modal so it doesn't weirdly
                  // overlap and show through
                  activeLineReader?.unmount();
                  return setIsContextModalOpen(true);
                }}
              >
                Read Passages
              </SecondaryButton>
            </ItemPassageToolbar>
          )}
          <div
            style={{ padding: "1rem", position: "relative" }}
            // onClickCapture means we get the event before the item does.
            // We use this to prevent click-through to the item while line
            // reader is enabled
            onClickCapture={handleClick}
            ref={(el) => {
              containerRef.current = el;

              if (el) {
                highlighterRef.current = new Highlighter(el, [
                  ".item-player-prompt",
                  ".item-player-stimulus",
                ]);
                onHighlighterInit(props.data.id, highlighterRef.current);
              } else {
                highlighterRef.current = null;
              }
            }}
          >
            <HighlighterToolbar highlighterRef={highlighterRef} />
            <ItemNumber>
              <span>
                Question ID: <span>{state.question?.idInc}</span>
              </span>
            </ItemNumber>
            <Canvas />
          </div>
        </ItemMain>
      </StyledItem>
    </QuestionContext.Provider>
  );
};

export default Item;
